import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";

/**
 * Themed multi-select popover. Replaces native
 * `<select multiple>`, whose listbox can't be themed via CSS.
 *
 * Trigger shows a comma-joined summary of selected labels (or
 * placeholder). The popover renders a checkbox list, portaled to
 * `document.body` and anchored to the trigger so it escapes
 * scroll-clipping ancestors (e.g. the focus-mode tools panel).
 */

export type TkticMultiSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type TkticMultiSelectProps = {
  value: string[];
  onChange: (next: string[]) => void;
  options: TkticMultiSelectOption[];
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  id?: string;
  name?: string;
  "data-testid"?: string;
  className?: string;
  /** Extra attributes forwarded to the hidden mirror
   * `<select multiple>` (e.g. test selectors that target the
   * select element by attribute). */
  nativeSelectProps?: Record<string, string | number | boolean> & {
    style?: CSSProperties;
  };
};

export function TkticMultiSelect({
  value,
  onChange,
  options,
  disabled = false,
  placeholder = "Select…",
  ariaLabel,
  id,
  name,
  "data-testid": dataTestId,
  className = "",
  nativeSelectProps,
}: TkticMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const popupId = useId();
  const [activeIdx, setActiveIdx] = useState<number>(-1);
  const typeBufRef = useRef<{ text: string; until: number }>({ text: "", until: 0 });

  useEffect(() => {
    if (!open) {
      setActiveIdx(-1);
      return;
    }
    // Start at the first selected option, else the first option.
    const firstSelected = options.findIndex(
      (o) => !o.disabled && value.includes(o.value),
    );
    setActiveIdx(firstSelected >= 0 ? firstSelected : 0);
  }, [open, options, value]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popupRef.current?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener("mousedown", onPointer);
    return () => window.removeEventListener("mousedown", onPointer);
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const compute = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    compute();
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
    };
  }, [open]);

  const selectedSet = new Set(value);
  const selectedLabels = options
    .filter((o) => selectedSet.has(o.value))
    .map((o) => o.label);
  const triggerLabel =
    selectedLabels.length === 0 ? (
      <span className="text-ink-muted">{placeholder}</span>
    ) : (
      <span className="truncate">
        {selectedLabels.length === 1
          ? selectedLabels[0]
          : `${selectedLabels.length} selected`}
      </span>
    );

  const toggle = (optValue: string) => {
    if (selectedSet.has(optValue)) {
      onChange(value.filter((v) => v !== optValue));
    } else {
      onChange([...value, optValue]);
    }
  };

  const activeOptionId =
    open && activeIdx >= 0 && activeIdx < options.length
      ? `${popupId}-opt-${options[activeIdx].value}`
      : undefined;

  const moveActive = (from: number, delta: 1 | -1): number => {
    if (options.length === 0) return -1;
    let i = from;
    for (let step = 0; step < options.length; step++) {
      i = (i + delta + options.length) % options.length;
      if (!options[i].disabled) return i;
    }
    return from;
  };

  const handleTypeahead = (ch: string) => {
    const now = Date.now();
    const buf =
      now < typeBufRef.current.until
        ? typeBufRef.current.text + ch
        : ch;
    typeBufRef.current = { text: buf, until: now + 500 };
    const match = options.findIndex(
      (o) => !o.disabled && o.label.toLowerCase().startsWith(buf.toLowerCase()),
    );
    if (match >= 0) setActiveIdx(match);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIdx((i) => moveActive(i < 0 ? -1 : i, 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIdx((i) => moveActive(i < 0 ? options.length : i, -1));
        break;
      case "Home":
        e.preventDefault();
        setActiveIdx(moveActive(-1, 1));
        break;
      case "End":
        e.preventDefault();
        setActiveIdx(moveActive(options.length, -1));
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        // Multi-select: Space/Enter TOGGLES the active option,
        // leaving the popup open so the user can keep choosing.
        if (activeIdx >= 0 && !options[activeIdx].disabled) {
          toggle(options[activeIdx].value);
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        break;
      case "Tab":
        setOpen(false);
        break;
      default:
        if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
          handleTypeahead(e.key);
        }
    }
  };

  return (
    <>
      {/* Hidden native <select multiple> kept in sync. Lets
       * Playwright's selectOption() etc. keep working on the same
       * data-testid the old native select used.
       * `nativeSelectProps` forwards any extra attrs needed by
       * attribute-based test selectors. */}
      <select
        {...nativeSelectProps}
        multiple
        aria-hidden="true"
        tabIndex={-1}
        name={name}
        value={value}
        onChange={(e) => {
          const next = Array.from(e.target.selectedOptions, (o) => o.value);
          onChange(next);
        }}
        disabled={disabled}
        data-testid={dataTestId}
        style={{
          position: "absolute",
          opacity: 0,
          pointerEvents: "none",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          ...(nativeSelectProps?.style ?? {}),
        }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? popupId : undefined}
        aria-activedescendant={activeOptionId}
        aria-label={ariaLabel}
        data-testid={dataTestId ? `${dataTestId}-trigger` : undefined}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={handleKeyDown}
        className={`flex w-full items-center justify-between gap-2 rounded border border-rule bg-bg-card px-2 py-1.5 text-sm text-ink hover:border-accent/60 focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 transition-colors ${className}`}
      >
        <span className="truncate text-left">{triggerLabel}</span>
        <ChevronIcon open={open} />
      </button>
      {open && pos && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={popupRef}
              role="listbox"
              aria-multiselectable="true"
              id={popupId}
              aria-label={ariaLabel}
              aria-activedescendant={activeOptionId}
              data-testid={dataTestId ? `${dataTestId}-popup` : undefined}
              style={{
                position: "fixed",
                top: pos.top,
                left: pos.left,
                minWidth: pos.width,
              }}
              className="z-[200] max-h-72 overflow-y-auto rounded border border-rule bg-surface shadow-lg py-1"
            >
              {options.map((opt, idx) => {
                const isSelected = selectedSet.has(opt.value);
                const isActive = idx === activeIdx;
                return (
                  <button
                    key={opt.value}
                    id={`${popupId}-opt-${opt.value}`}
                    type="button"
                    role="option"
                    tabIndex={-1}
                    ref={
                      isActive
                        ? (el) => {
                            el?.scrollIntoView({ block: "nearest" });
                          }
                        : undefined
                    }
                    aria-selected={isSelected}
                    disabled={opt.disabled}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => toggle(opt.value)}
                    data-testid={
                      dataTestId
                        ? `${dataTestId}-option-${opt.value}`
                        : undefined
                    }
                    className={[
                      "flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm transition-colors",
                      isActive ? "bg-accent/20" : isSelected ? "bg-accent/10" : "",
                      opt.disabled ? "opacity-40 cursor-not-allowed" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <Checkbox checked={isSelected} />
                    <span className="flex-1 truncate text-ink">{opt.label}</span>
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function Checkbox({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
        checked ? "border-accent bg-accent text-white" : "border-rule bg-bg-card"
      }`}
    >
      {checked ? (
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : null}
    </span>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{
        transition: "transform 120ms ease",
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
      }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
